import { camelCase, paramCase } from 'change-case';

type KebabCaseToPascalCase<S extends string> =
	S extends `${infer FirstWord}-${infer Rest}`
		? `${Capitalize<Lowercase<FirstWord>>}${KebabCaseToPascalCase<Rest>}`
		: Capitalize<Lowercase<S>>;
type KebabCaseToCamelCase<S extends string> = S extends `${infer FirstWord}-${infer Rest}`
	? `${Lowercase<FirstWord>}${KebabCaseToPascalCase<Rest>}`
	: `${Lowercase<S>}`;

/**
 * Produces an object that can be used  to write classnames out around your component.
 * The same object can be shared to the tailwind class in order to gain ease of rule writing with classname autocomplete
 * and referential integrity.  Ensures camelCase propnames associate with the kebabcase classname inputs.
 * 
 * example...
 * input:  base='sds-button-icon' otherClassNames=['is-enabled','variant-tertiary','variant-secondary']
 * output: {
 *		 base : "sds-button-icon",
 *		 isEnabled: "is-enabled",
 *		 variantSecondary: "variant-secondary"
 *		 variantTertiary: "variant-tertiary"
 * }
 * 
 * @param base 
 * @param otherClassNames 
 * @returns 
 */
export const getClassNames = <OtherClassName extends string>(
	base: string,
	otherClassNames?: OtherClassName[]
) => {
	return {
		base,
		...((otherClassNames
			? otherClassNames.reduce((prev, current) => {
					return { ...prev, ...{ [camelCase(current)]: current } };
			  }, {})
			: {}) as { [key in KebabCaseToCamelCase<OtherClassName>]: string }),
	};
};

/**
 * Generarates an array of clasnames to feed into the 'classnames' dependency
 * which runs on root elements of components.
 * 
 * It infers stuff from a combination of the incomming classnames list (produced with 
 * the above getClassNames function and takes the component's props.  By looking at inbound prop values
 * we can write out correct boolean flag classnames e.g. {isEnabled: true} = className={'is-enabled'}
 * This generates a consistent set of classes based on variant props and boolean props.
 * 
 * example...
 * input: 
 * classNames={
 *		 base : "sds-button-icon",
 *		 isEnabled: "is-enabled",
 *		 variantPrimary: "variant-primary",
 *		 variantSecondary: "variant-secondary",
 *		 variantTertiary: "variant-tertiary",
 *		 sizeLogo: "size-logo",
 *       sizeSmall: "size-small",
 *       sizeStandard: "size-standard"
 * } 
 * props={
 *  "isEnabled": true,
 *  "variant": "secondary",
 *  "size": "small"
 * }
 * 
 * output: ["sds-button-icon", {is-enabled: true},"variant-secondary","size-small"]
 *
 * @param classNames
 * @param props
 * @returns
 */

export const getRootClassListFromProps = (
	classNames: { [key: string]: string },
	props: { [key: string]: any }
) => {
	const classNameArray = [
		// 1st get base name from classNames e.g. 'sds-button-icon'
		classNames.base,
		...(props.className ? [props.className] : []),
		// now generate classes from booleans or variants
		...Object.entries(props)
		.filter(([, propValue]) => propValue!==undefined )
			.map(([propName, propValue]) => {
				const matchingClassname = classNames[propName];
				if (typeof propValue === 'boolean' && matchingClassname) {
					//e.g prop of isEnabled: true will output 'is-enabled'
					return [{ [matchingClassname]: propValue }];
				} else if (
					Object.values(classNames)
						.filter((className) => className.includes('-'))
						.map((className) => className.split('-')[0])
						.includes(propName)
				) {
					if (typeof propValue != 'string') {
						//Fix issue when propValue is a variant object
						if (propValue.type && typeof propValue.type === 'string') {
							//variant objects typically have a variant.type
							return [`${propName}-${paramCase(propValue.type)}`];
						} else {
							return [`${propName}-${paramCase(typeof propValue)}`];
						}
					}
					return [`${propName}-${paramCase(propValue)}`];
				} else {
					return [];
				}
			})
			.flat(),
	];
	return classNameArray;
};
